import React from 'react';

import {
  IconArchive,
  IconBrandStripe,
  IconCalendar,
  IconDashboard,
  IconSettings,
  IconUserCircle,
} from '@tabler/icons-react';
import DesktopLayout from 'layouts/DesktopLayout';
import MobileLayout from 'layouts/MobileLayout';
import { Navigate } from 'react-router-dom';
import { loadingRouteMap } from 'routes';
import { SessionGym } from 'types';
import { DEFAULT_PROGRAM_NAME } from 'utils/checkInProgram';
import { isWorkoutTrackerEnabled } from 'utils/workoutTracker';

import { INavLink, IRouteMap } from './types';

export const staffRouteMap: IRouteMap = {
  layout: DesktopLayout,
  routes: [
    {
      key: 'dashboard',
      component: React.lazy(() => import('pages/private/owner/Dashboard')),
      path: '/',
    },
    {
      key: 'arm',
      component: React.lazy(() => import('pages/private/owner/reports/ArmReport')),
      path: '/reports/arm/:type',
    },
    {
      key: 'total-revenue',
      component: React.lazy(() => import('pages/private/owner/reports/TotalRevenue')),
      path: '/reports/total-revenue',
    },
    {
      key: 'upcoming-revenue',
      component: React.lazy(() => import('pages/private/owner/reports/UpcomingRevenue')),
      path: '/reports/upcoming-revenue',
    },
    {
      key: 'pending-payments',
      component: React.lazy(() => import('pages/private/owner/reports/PendingPayments')),
      path: '/reports/pending-payments',
    },
    {
      key: 'past-due-payments',
      component: React.lazy(() => import('pages/private/owner/reports/PastDuePayments')),
      path: '/reports/past-due-payments',
    },
    {
      key: 'total-members',
      component: React.lazy(() => import('pages/private/owner/reports/TotalMembers')),
      path: '/reports/total-members',
    },
    {
      key: 'paying-members',
      component: React.lazy(() => import('pages/private/owner/reports/PayingMembers')),
      path: '/reports/paying-members',
    },
    {
      key: 'new-members',
      component: React.lazy(() => import('pages/private/owner/reports/NewMembers')),
      path: '/reports/new-members',
    },
    {
      key: 'cancelled-members',
      component: React.lazy(() => import('pages/private/owner/reports/CancelledMembers')),
      path: '/reports/cancelled-members',
    },
    {
      key: 'missing-athletes',
      component: React.lazy(() => import('pages/private/owner/reports/MissingAthletes')),
      path: '/reports/missing-athletes',
    },
    {
      key: 'reactivated-members',
      component: React.lazy(() => import('pages/private/owner/reports/ReactivatedMembers')),
      path: '/reports/reactivated-members',
    },
    {
      key: 'trial-members',
      component: React.lazy(() => import('pages/private/owner/reports/TrialMembers')),
      path: '/reports/trial-members',
    },
    {
      key: 'cancelled-trial-members',
      component: React.lazy(() => import('pages/private/owner/reports/CancelledTrialMembers')),
      path: '/reports/cancelled-trial-members',
    },
    {
      key: 'athletes-on-hold',
      component: React.lazy(() => import('pages/private/owner/reports/AthletesOnHold')),
      path: '/reports/athletes-on-hold',
    },
    {
      key: 'athletes-at-risk',
      component: React.lazy(() => import('pages/private/owner/reports/AthletesAtRisk')),
      path: '/reports/athletes-at-risk',
    },
    {
      key: 'athlete-attendance',
      component: React.lazy(() => import('pages/private/owner/reports/AthleteAttendance')),
      path: '/reports/athlete-attendance',
    },
    {
      key: 'athlete-birthdays',
      component: React.lazy(() => import('pages/private/owner/reports/AthleteBirthdays')),
      path: '/reports/athlete-birthdays',
    },
    {
      key: 'package-revenue',
      component: React.lazy(() => import('pages/private/owner/reports/PackageRevenue')),
      path: '/reports/package-revenue',
    },
    {
      key: 'package-category-revenue',
      component: React.lazy(() => import('pages/private/owner/reports/PackageCategoryRevenue')),
      path: '/reports/package-category-revenue',
    },
    {
      key: 'class-usage',
      component: React.lazy(() => import('pages/private/owner/reports/ClassUsage')),
      path: '/reports/class-usage',
    },
    {
      key: 'package-utilization',
      component: React.lazy(() => import('pages/private/owner/reports/PackageUtilization')),
      path: '/reports/package-utilization',
    },
    {
      key: 'uncredited-reservations',
      component: React.lazy(() => import('pages/private/owner/reports/UncreditedReservations')),
      path: '/reports/uncredited-reservations',
    },
    {
      key: 'kiosk',
      component: React.lazy(() => import('pages/private/owner/Kiosk')),
      path: '/kiosk',
    },
    {
      key: 'kioskDetail',
      component: React.lazy(() => import('pages/private/owner/KioskDetail')),
      path: '/kiosk/:eventId',
    },
    {
      key: 'kioskWorkout',
      component: React.lazy(() => import('pages/private/owner/KioskWorkout')),
      path: '/kiosk/:eventId/workout',
    },
    {
      key: 'scheduling',
      component: React.lazy(() => import('pages/private/owner/Scheduling')),
      path: '/calendar/scheduling',
    },
    {
      key: 'programming',
      component: React.lazy(() => import('pages/private/owner/Program')),
      path: '/calendar/programming',
    },
    {
      key: 'availability',
      component: React.lazy(() => import('pages/private/owner/Availability')),
      path: '/calendar/availability',
    },
    {
      key: 'workout-library',
      component: React.lazy(() => import('pages/private/owner/Workouts/Library')),
      path: '/workouts/library',
    },
    {
      key: 'create-workout',
      component: React.lazy(() => import('pages/private/owner/Workouts/Programming/pages/CreateWorkout')),
      path: '/workouts/programming/create',
    },
    {
      key: 'edit-workout',
      component: React.lazy(() => import('pages/private/owner/Workouts/Programming/pages/EditWorkout')),
      path: '/workouts/programming/edit/:workoutId',
    },
    {
      key: 'create-workout-template',
      component: React.lazy(
        () => import('pages/private/owner/Workouts/Library/pages/CreateWorkoutTemplate'),
      ),
      path: '/workouts/library/templates/create',
    },
    {
      key: 'edit-workout-template',
      component: React.lazy(() => import('pages/private/owner/Workouts/Library/pages/EditWorkoutTemplate')),
      path: '/workouts/library/templates/edit/:workoutTemplateId',
    },
    {
      key: 'workout-programming',
      component: React.lazy(() => import('pages/private/owner/Workouts/Programming')),
      path: '/workouts/programming',
    },
    {
      key: 'workout-whiteboard',
      component: React.lazy(() => import('pages/private/owner/Workouts/Whiteboard')),
      path: '/workouts/whiteboard',
    },
    {
      key: 'integrations',
      component: React.lazy(() => import('pages/private/owner/Integration')),
      path: '/integrations/google-calendar',
    },
    {
      key: 'claude-integration',
      component: React.lazy(() => import('pages/private/owner/ClaudeIntegration')),
      path: '/integrations/claude',
    },
    {
      key: 'claude-select-profile',
      component: React.lazy(() => import('pages/private/owner/ClaudeSelectProfile')),
      path: '/integrations/claude/select-profile',
    },
    {
      key: 'packages',
      component: React.lazy(() => import('pages/private/owner/Package')),
      path: '/products/packages',
    },
    {
      key: 'package-categories',
      component: React.lazy(() => import('pages/private/owner/PackageCategory')),
      path: '/products/package-categories',
    },
    {
      key: 'discounts',
      component: React.lazy(() => import('pages/private/owner/Discounts')),
      path: '/products/discounts',
    },
    {
      key: 'check-in-program-settings',
      component: React.lazy(() => import('pages/private/owner/CommittedClub/Settings')),
      path: '/check-in-program/settings',
    },
    {
      key: 'check-in-program-tracking',
      component: React.lazy(() => import('pages/private/owner/CommittedClub/Tracking')),
      path: '/check-in-program/tracking',
    },
    {
      key: 'retail',
      component: React.lazy(() => import('pages/private/owner/Retail')),
      path: '/products/retail',
    },
    {
      key: 'fees-and-misc',
      component: React.lazy(() => import('pages/private/owner/FeesAndMiscProducts')),
      path: '/products/fees-and-misc-products',
    },
    {
      key: 'tax-rates',
      component: React.lazy(() => import('pages/private/owner/TaxRates')),
      path: '/products/tax-rates',
    },
    {
      key: 'sales-portal',
      component: React.lazy(() => import('pages/private/owner/SalesPortalConfiguration')),
      path: '/products/sales-portal',
    },

    {
      key: 'athletes',
      component: React.lazy(() => import('pages/private/owner/Athletes')),
      path: '/users/athletes',
    },
    {
      key: 'athlete-detail',
      component: React.lazy(() => import('pages/private/owner/AthleteDetail')),
      path: '/users/athletes/:athleteId/:activeTab?',
    },
    {
      key: 'staff',
      component: React.lazy(() => import('pages/private/owner/Staff')),
      path: '/users/staff',
    },
    {
      key: 'classes',
      component: React.lazy(() => import('pages/private/owner/ClassType')),
      path: '/events/classes',
    },
    {
      key: 'appointments',
      component: React.lazy(() => import('pages/private/owner/AppointmentType')),
      path: '/events/appointments',
    },
    {
      key: 'location',
      component: React.lazy(() => import('pages/private/owner/Location')),
      path: '/events/location',
    },
    {
      key: 'issued-documents',
      component: React.lazy(() => import('pages/private/owner/IssuedDocuments')),
      path: '/documents/issued',
    },
    {
      key: 'document-templates',
      component: React.lazy(() => import('pages/private/owner/DocumentTemplates')),
      path: '/documents/templates',
    },
    {
      key: 'invoices',
      component: React.lazy(() => import('pages/private/owner/Invoice')),
      path: '/invoices',
    },
    {
      key: 'payroll-report-own',
      component: React.lazy(() => import('pages/private/owner/PayrollReportOwn')),
      path: '/payroll/payroll-report-own',
    },
    {
      key: 'payroll-report-all',
      component: React.lazy(() => import('pages/private/owner/PayrollReport')),
      path: '/payroll/payroll-report',
    },
    {
      key: 'pay-rates',
      component: React.lazy(() => import('pages/private/owner/PayRates')),
      path: '/payroll/pay-rates',
    },
    {
      key: 'assign-rates',
      component: React.lazy(() => import('pages/private/owner/AssignRates')),
      path: '/payroll/assign-rates',
    },
    {
      key: 'flows',
      component: React.lazy(() => import('pages/private/owner/Flows')),
      path: 'flows',
    },
    {
      key: 'flow-builder',
      component: React.lazy(() => import('pages/private/owner/FlowBuilder')),
      path: 'flow-builder',
    },
    {
      key: 'flow-builder-edit',
      component: React.lazy(() => import('pages/private/owner/FlowBuilder')),
      path: '/flow-builder/:flowId',
    },
    {
      key: 'flow-templates',
      component: React.lazy(() => import('pages/private/owner/NotificationMessageTemplates')),
      path: '/flows/templates',
    },
    {
      key: 'glm-integration',
      component: React.lazy(() => import('pages/private/owner/KiloGLMFlows')),
      path: '/integrations/glm/',
    },
    {
      key: 'glm-flow-builder',
      component: React.lazy(() => import('pages/private/owner/KiloGLMFlowBuilder')),
      path: '/integrations/glm/builder',
    },
    {
      key: 'glm-flow-builder-edit',
      component: React.lazy(() => import('pages/private/owner/KiloGLMFlowBuilder')),
      path: '/integrations/glm/builder/:flowId',
    },
    {
      key: 'glm-kilo-flow-builder',
      component: React.lazy(() => import('pages/private/owner/GLMKiloFlowBuilder')),
      path: '/integrations/glm/glm-builder',
    },
    {
      key: 'glm-kilo-flow-builder-edit',
      component: React.lazy(() => import('pages/private/owner/GLMKiloFlowBuilder')),
      path: '/integrations/glm/glm-builder/:flowId',
    },
    {
      key: 'work-hours',
      component: React.lazy(() => import('pages/private/owner/WorkHours')),
      path: '/work-hours',
    },
    {
      key: 'gym-settings',
      component: React.lazy(() => import('pages/private/owner/GymSettings')),
      path: '/gym-settings',
    },
    {
      key: 'onboarding-checklist',
      component: React.lazy(() => import('pages/private/owner/OnboardingChecklist')),
      path: '/onboarding-checklist',
    },
    {
      key: 'stripe-dashboard',
      component: React.lazy(() => import('pages/private/owner/StripeDashboard')),
      path: '/stripe-dashboard',
    },
    {
      key: 'redirect',
      component: () => Navigate({ to: '/' }),
      path: '*',
    },
  ],
  navLinks: [
    { key: 'dashboard', label: 'Dashboard', route: '/' },
    { key: 'kiosk', label: 'Kiosk', route: '/kiosk' },
    {
      key: 'calendar',
      label: 'Calendar',
      route: '/calendar',
      subLinks: [
        { key: 'scheduling', label: 'Scheduling', route: '/calendar/scheduling' },
        { key: 'programming', label: 'Programming', route: '/calendar/programming' },
        { key: 'availability', label: 'Availability', route: '/calendar/availability' },
      ],
    },
    {
      key: 'workouts',
      label: 'Workouts',
      route: '/workouts',
      subLinks: [
        { key: 'workout-programming', label: 'Programming', route: '/workouts/programming' },
        { key: 'workout-library', label: 'Library', route: '/workouts/library' },
        { key: 'workout-whiteboard', label: 'Whiteboard', route: '/workouts/whiteboard' },
      ],
    },
    {
      key: 'products',
      label: 'Products',
      route: '/products',
      subLinks: [
        { key: 'packages', label: 'Packages', route: '/products/packages' },
        { key: 'package-categories', label: 'Package Categories', route: '/products/package-categories' },
        {
          key: 'discounts',
          label: 'Discounts',
          route: '/products/discounts',
        },
        { key: 'retail', label: 'Retail', route: '/products/retail' },
        {
          key: 'fees-and-misc',
          label: 'Fees & Misc Products',
          route: '/products/fees-and-misc-products',
        },
        { key: 'tax-rates', label: 'Tax Rates', route: '/products/tax-rates' },
        {
          key: 'sales-portal',
          label: 'Sales Portal',
          route: '/products/sales-portal',
        },
      ],
    },
    {
      key: 'check-in-program',
      label: DEFAULT_PROGRAM_NAME,
      route: '/check-in-program/tracking',
    },
    {
      key: 'users',
      label: 'Users',
      route: '/users',
      subLinks: [
        { key: 'athletes', label: 'Athletes', route: '/users/athletes' },
        { key: 'staff', label: 'Staff', route: '/users/staff' },
      ],
    },
    {
      key: 'events',
      label: 'Events',
      route: '/events',
      subLinks: [
        { key: 'classes', label: 'Classes', route: '/events/classes' },
        { key: 'appointments', label: 'Appointments', route: '/events/appointments' },
        { key: 'location', label: 'Locations', route: '/events/location' },
      ],
    },
    {
      key: 'documents',
      label: 'Documents',
      route: '/documents',
      subLinks: [
        { key: 'issued-documents', label: 'Issued', route: '/documents/issued' },
        { key: 'document-templates', label: 'Templates', route: '/documents/templates' },
      ],
    },
    { key: 'invoices', label: 'Invoices', route: '/invoices' },
    {
      key: 'payroll',
      label: 'Payroll',
      route: '/payroll',
      subLinks: [
        { key: 'work-hours', label: 'Work Hours', route: '/work-hours' },
        { key: 'payroll-report-own', label: 'My Payroll Report', route: '/payroll/payroll-report-own' },
        { key: 'payroll-report-all', label: 'Staff Payroll Report', route: '/payroll/payroll-report' },
        { key: 'pay-rates', label: 'Pay Rates', route: '/payroll/pay-rates' },
        { key: 'assign-rates', label: 'Assign Rates', route: '/payroll/assign-rates' },
      ],
    },
    {
      key: 'notifications',
      label: 'Notifications',
      route: '/flows',
      subLinks: [
        { key: 'flows', label: 'Automation Flows', route: '/flows' },
        { key: 'flow-templates', label: 'Message Templates', route: '/flows/templates' },
        { key: 'flow-builder', label: 'Automation Flow Builder', route: '/flow-builder' },
      ],
    },
    {
      key: 'integrations',
      label: 'Integrations',
      route: '/integrations',
      subLinks: [
        { key: 'glm-integration', label: 'Gym Lead Machine', route: '/integrations/glm/' },
        {
          key: 'google-calendar-integration',
          label: 'Google Calendar',
          route: '/integrations/google-calendar',
        },
        { key: 'claude-integration', label: 'Claude AI', route: '/integrations/claude' },
      ],
    },
    { key: 'stripe-dashboard', label: 'Stripe Dashboard', route: '/stripe-dashboard' },
  ],
};

export const staffMobileRouteMap: IRouteMap = {
  layout: MobileLayout,
  routes: [
    {
      key: 'claude-integration',
      component: React.lazy(() => import('pages/private/owner/ClaudeIntegration')),
      path: '/integrations/claude',
    },
    {
      key: 'claude-select-profile',
      component: React.lazy(() => import('pages/private/owner/ClaudeSelectProfile')),
      path: '/integrations/claude/select-profile',
    },
    {
      key: 'scheduling',
      component: React.lazy(() => import('pages/private/ownerMobile/Schedule')),
      path: '/',
    },
    {
      key: 'kiosk',
      component: React.lazy(() => import('pages/private/owner/Kiosk')),
      path: '/kiosk',
    },
    {
      key: 'kioskDetail',
      component: React.lazy(() => import('pages/private/owner/KioskDetail')),
      path: '/kiosk/:eventId',
    },
    {
      key: 'kioskWorkout',
      component: React.lazy(() => import('pages/private/owner/KioskWorkout')),
      path: '/kiosk/:eventId/workout',
    },
    {
      key: 'athletes',
      component: React.lazy(() => import('pages/private/ownerMobile/Athletes')),
      path: '/athletes',
    },
    {
      key: 'athlete-detail',
      component: React.lazy(() => import('pages/private/ownerMobile/AthleteDetail')),
      path: '/users/athletes/:athleteId/:activeTab?',
    },
    {
      key: 'products',
      component: React.lazy(() => import('pages/private/ownerMobile/Products')),
      path: '/products',
    },
    {
      key: 'package-detail',
      component: React.lazy(() => import('pages/private/ownerMobile/PackageDetail')),
      path: '/packages/:packageId',
    },
    {
      key: 'dashboard',
      component: React.lazy(() => import('pages/private/ownerMobile/Dashboard')),
      path: '/dashboard',
    },
    {
      key: 'arm',
      component: React.lazy(() => import('pages/private/owner/reports/ArmReport')),
      path: '/reports/arm/:type',
    },
    {
      key: 'total-revenue',
      component: React.lazy(() => import('pages/private/owner/reports/TotalRevenue')),
      path: '/reports/total-revenue',
    },
    {
      key: 'upcoming-revenue',
      component: React.lazy(() => import('pages/private/owner/reports/UpcomingRevenue')),
      path: '/reports/upcoming-revenue',
    },
    {
      key: 'pending-payments',
      component: React.lazy(() => import('pages/private/owner/reports/PendingPayments')),
      path: '/reports/pending-payments',
    },
    {
      key: 'past-due-payments',
      component: React.lazy(() => import('pages/private/owner/reports/PastDuePayments')),
      path: '/reports/past-due-payments',
    },
    {
      key: 'total-members',
      component: React.lazy(() => import('pages/private/owner/reports/TotalMembers')),
      path: '/reports/total-members',
    },
    {
      key: 'paying-members',
      component: React.lazy(() => import('pages/private/owner/reports/PayingMembers')),
      path: '/reports/paying-members',
    },
    {
      key: 'new-members',
      component: React.lazy(() => import('pages/private/owner/reports/NewMembers')),
      path: '/reports/new-members',
    },
    {
      key: 'cancelled-members',
      component: React.lazy(() => import('pages/private/owner/reports/CancelledMembers')),
      path: '/reports/cancelled-members',
    },
    {
      key: 'missing-athletes',
      component: React.lazy(() => import('pages/private/owner/reports/MissingAthletes')),
      path: '/reports/missing-athletes',
    },
    {
      key: 'reactivated-members',
      component: React.lazy(() => import('pages/private/owner/reports/ReactivatedMembers')),
      path: '/reports/reactivated-members',
    },
    {
      key: 'trial-members',
      component: React.lazy(() => import('pages/private/owner/reports/TrialMembers')),
      path: '/reports/trial-members',
    },
    {
      key: 'cancelled-trial-members',
      component: React.lazy(() => import('pages/private/owner/reports/CancelledTrialMembers')),
      path: '/reports/cancelled-trial-members',
    },
    {
      key: 'athletes-on-hold',
      component: React.lazy(() => import('pages/private/owner/reports/AthletesOnHold')),
      path: '/reports/athletes-on-hold',
    },
    {
      key: 'athletes-at-risk',
      component: React.lazy(() => import('pages/private/owner/reports/AthletesAtRisk')),
      path: '/reports/athletes-at-risk',
    },
    {
      key: 'athlete-attendance',
      component: React.lazy(() => import('pages/private/owner/reports/AthleteAttendance')),
      path: '/reports/athlete-attendance',
    },
    {
      key: 'athlete-birthdays',
      component: React.lazy(() => import('pages/private/owner/reports/AthleteBirthdays')),
      path: '/reports/athlete-birthdays',
    },
    {
      key: 'class-usage',
      component: React.lazy(() => import('pages/private/owner/reports/ClassUsage')),
      path: '/reports/class-usage',
    },
    {
      key: 'package-utilization',
      component: React.lazy(() => import('pages/private/owner/reports/PackageUtilization')),
      path: '/reports/package-utilization',
    },
    {
      key: 'uncredited-reservations',
      component: React.lazy(() => import('pages/private/owner/reports/UncreditedReservations')),
      path: '/reports/uncredited-reservations',
    },
    {
      key: 'account',
      component: React.lazy(() => import('pages/private/ownerMobile/AccountDetails')),
      path: '/account',
    },
    {
      key: 'stripe-dashboard',
      component: React.lazy(() => import('pages/private/owner/StripeDashboard')),
      path: '/stripe-dashboard',
    },
    {
      key: 'redirect',
      component: () => Navigate({ to: '/' }),
      path: '*',
    },
  ],
  navLinks: [
    {
      key: 'dashboard',
      Icon: IconDashboard,
      label: 'Dashboard',
      route: '/dashboard',
    },
    {
      key: 'athletes',
      Icon: IconUserCircle,
      label: 'Athletes',
      route: '/athletes',
    },
    {
      key: 'schedule',
      Icon: IconCalendar,
      label: 'Schedule',
      route: '/',
    },
    {
      key: 'products',
      Icon: IconArchive,
      label: 'Products',
      route: '/products',
    },
    {
      key: 'account',
      Icon: IconSettings,
      label: 'Account',
      route: '/account',
    },
    {
      key: 'stripe-dashboard',
      Icon: IconBrandStripe,
      label: 'Stripe',
      route: '/stripe-dashboard',
    },
  ],
};

export const onboardingRouteMap: IRouteMap = {
  layout: DesktopLayout,
  routes: [
    {
      key: 'onboarding',
      component: React.lazy(() => import('pages/private/owner/OnboardingPage')),
      path: '/',
    },
    {
      key: 'redirect',
      component: () => Navigate({ to: '/' }),
      path: '*',
    },
  ],
  navLinks: [
    {
      key: 'onboarding',
      label: 'Onboarding',
      route: '/',
    },
  ],
};

const routeKeyToPermissionMap: { [key: string]: string | null } = {
  dashboard: null,
  arm: 'BusinessMetricsReportsView',
  'total-revenue': 'AccountingReportsView',
  'upcoming-revenue': 'AccountingReportsView',
  'pending-payments': 'PaymentReportsView',
  'past-due-payments': 'PaymentReportsView',
  'total-members': 'AthleteReportsView',
  'paying-members': 'AthleteReportsView',
  'new-members': 'AthleteReportsView',
  'cancelled-members': 'AthleteReportsView',
  'missing-athletes': 'AthleteReportsView',
  'reactivated-members': 'AthleteReportsView',
  'trial-members': 'AthleteReportsView',
  'cancelled-trial-members': 'AthleteReportsView',
  'athletes-on-hold': 'AthleteReportsView',
  'athletes-at-risk': 'AthleteReportsView',
  'athlete-attendance': null,
  'athlete-birthdays': null,
  'package-revenue': 'PackageReportsView',
  'package-category-revenue': 'PackageReportsView',
  'class-usage': 'UtilizationReportsView',
  'package-utilization': 'UtilizationReportsView',
  'uncredited-reservations': 'ReservationsReportView',
  kiosk: 'Kiosk',
  kioskWorkout: 'Kiosk',
  scheduling: null,
  programming: null,
  packages: null,
  retail: null,
  'fees-and-misc': null,
  'tax-rates': null,
  'sales-portal': null,
  athletes: 'AthleteView',
  'athlete-detail': 'AthleteView',
  staff: 'StaffView',
  classes: null,
  appointments: null,
  location: null,
  workout: null,
  'issued-documents': null,
  'document-templates': null,
  invoices: 'InvoiceReportsView',
  'payroll-report-all': 'PayrollReportViewAll',
  'payroll-report-own': 'PayrollReportViewOwn',
  'pay-rates': 'PayrollMutate',
  discounts: null,
  'check-in-program': 'CheckInProgramView',
  'check-in-program-settings': 'CheckInProgramView',
  'check-in-program-tracking': 'CheckInProgramView',
  'assign-rates': 'PayrollMutate',
  flows: 'FlowView',
  'flow-builder': 'FlowMutate',
  'flow-builder-edit': 'FlowView',
  'flow-templates': 'FlowView',
  'work-hours': null,
  'gym-settings': 'GymMutate',
  'onboarding-checklist': null,
  // Required, not optional: an unmapped key is unrestricted. Both surfaces are owner-only
  // server-side too (authorizable-profiles 403s for everyone else).
  'claude-select-profile': 'MCPConnectionMutate',
  'claude-integration': 'MCPConnectionMutate',
  // Required, not optional: checkPermission treats an unmapped key as unrestricted, so leaving
  // this out would hand every staff member the Stripe dashboard.
  'stripe-dashboard': 'StripeDashboardView',
  redirect: null,
};

const checkPermission = (key: string, hasPerm: (permission: string) => boolean): boolean => {
  const permission = key in routeKeyToPermissionMap ? routeKeyToPermissionMap[key] : null;
  if (!permission) return true;
  return hasPerm(permission);
};

// TODO(MULTI_PROGRAM): temporary — naming the nav entry after *the* program only
// works while a gym has exactly one. With several there is no single name to derive
// from, and whatever names the collection does not exist as a concept yet. Expect to
// replace this, the session's check_in_program_name, and the page/toggle labels
// together rather than extend them.
const checkInProgramLabel = (name: string | null | undefined): string =>
  name?.trim() || DEFAULT_PROGRAM_NAME;

const removeRoutesAndLinks = (routeMap: IRouteMap, hasPerm: (permission: string) => boolean): IRouteMap => {
  const trimmedRoutes = routeMap.routes.filter(route => {
    return checkPermission(route.key, hasPerm);
  });

  const trimmedNavLinks = routeMap.navLinks.reduce((accum, navLink) => {
    if (navLink.subLinks) {
      const subLinks = navLink.subLinks.reduce((acc, subLink) => {
        const allowed = checkPermission(subLink.key, hasPerm);
        if (allowed) acc.push(subLink);
        return acc;
      }, [] as INavLink[]);
      if (subLinks.length) accum.push({ ...navLink, subLinks });
    } else {
      const allowed = checkPermission(navLink.key, hasPerm);
      if (allowed) accum.push(navLink);
    }
    return accum;
  }, [] as INavLink[]);

  return {
    layout: routeMap.layout,
    routes: trimmedRoutes,
    navLinks: trimmedNavLinks,
  };
};

export const getStaffRouteMap = (
  role: 'owner' | 'staff',
  gym: SessionGym | null,
  hasPerm: (permission: string) => boolean,
  isDesktop: boolean,
  // Whether GLM is available for this gym — a capability, not a permission. The caller
  // derives it (real connection OR simulated for demo gyms); this layer only needs to know
  // GLM is available, not why.
  // TODO: broader cleanup of gym-level conditions threaded through here as positional
  // booleans is tracked by a sustaining-engineering ticket (see PR discussion).
  glmEnabled = false,
): IRouteMap => {
  if (!gym) return loadingRouteMap;
  if (gym.stripeStatus === 'Onboard') {
    return onboardingRouteMap;
  }

  // The embedded dashboard is no longer restricted by Connect account type, so it lives in the
  // base maps and needs no fork here. Staff gating is the permission trimming below.
  let routeMap = isDesktop ? staffRouteMap : staffMobileRouteMap;
  if (role === 'staff') {
    routeMap = removeRoutesAndLinks(routeMap, hasPerm);
  }

  // Drop the GLM routes/nav when GLM is not available for this gym.
  if (!glmEnabled) {
    routeMap = {
      layout: routeMap.layout,
      routes: routeMap.routes.filter(
        route => !['glm-flows', 'glm-flow-builder', 'glm-flow-builder-edit'].includes(route.key),
      ),
      navLinks: routeMap.navLinks.filter(navLink => navLink.key !== 'glm-flows'),
    };
    routeMap.navLinks = routeMap.navLinks.map(navLink => {
      if (navLink.key === 'notifications' && navLink.subLinks) {
        return {
          ...navLink,
          subLinks: navLink.subLinks.filter(subLink => subLink.key !== 'glm-flows'),
        };
      }
      return navLink;
    });
  }

  routeMap = {
    ...routeMap,
    navLinks: routeMap.navLinks.map(navLink =>
      navLink.key === 'check-in-program'
        ? { ...navLink, label: checkInProgramLabel(gym.checkInProgramName) }
        : navLink,
    ),
  };

  // TODO(WORKOUT_TRACKER_REMOVAL): Remove flag check, make workout routes always visible
  const workoutEnabled = isWorkoutTrackerEnabled(gym.workoutTrackerEnabled);
  const isWorkoutRoute = (route: { key: string; path: string }): boolean =>
    route.path.startsWith('/workouts/') || route.key === 'kioskWorkout';

  // Workouts>Programming replaces Calendar>Programming when workout tracker is enabled
  if (workoutEnabled) {
    routeMap = {
      ...routeMap,
      navLinks: routeMap.navLinks.map(navLink =>
        navLink.key === 'calendar' && navLink.subLinks
          ? { ...navLink, subLinks: navLink.subLinks.filter(sub => sub.key !== 'programming') }
          : navLink,
      ),
    };
  } else {
    routeMap = {
      ...routeMap,
      routes: routeMap.routes.filter(route => !isWorkoutRoute(route)),
      navLinks: routeMap.navLinks.filter(navLink => navLink.key !== 'workouts'),
    };
  }

  return routeMap;
};
